// Frise de pousse : un mois passé et 6 jours de prévision ; la pluie tombe d'en haut, la pousse monte d'en bas,
// une courbe par espèce.
// Faire glisser le doigt dessus change le jour de la carte : c'est la seule animation continue de l'interface.
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { scaleLinear } from 'd3-scale';
import type { IsoDate, Species, TimelinePoint } from '../api/contract';
import { scoreLabel } from '../api/contract';
import { formatDayNumber, formatLong, formatWeekdayShort } from '../lib/dates';
import { SPECIES } from '../lib/species';
import './frise.css';

export interface FriseSeries {
  species: Species;
  days: TimelinePoint[];
}

interface Props {
  /** Vide pendant le chargement : l'en-tête est déjà affiché (et prérendu), la trace attend. */
  series: FriseSeries[];
  today: IsoDate | null;
  selected: IsoDate | null;
  onSelect: (date: IsoDate) => void;
  /** zone : score moyen de la zone visible ; maille : score de la maille, de 0 à 100. */
  mode: 'zone' | 'maille';
  label: string;
}

const RAIN_H = 26;
const GAP = 6;
const CURVE_H = 46;
const LABELS_H = 44;
const HEIGHT = RAIN_H + GAP + CURVE_H + LABELS_H;
const CURVE_TOP = RAIN_H + GAP;
const CURVE_BOTTOM = CURVE_TOP + CURVE_H;

/** Courbe lissée (Catmull-Rom vers Bézier), sans dépasser les valeurs extrêmes de façon visible. */
function smoothPath(points: [number, number][]): string {
  if (points.length === 0) return '';
  let d = `M${points[0]![0]},${points[0]![1]}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] ?? points[i]!;
    const p1 = points[i]!;
    const p2 = points[i + 1]!;
    const p3 = points[i + 2] ?? p2;
    const t = 0.18;
    const c1y = Math.min(CURVE_BOTTOM, Math.max(CURVE_TOP - 4, p1[1] + (p2[1] - p0[1]) * t));
    const c2y = Math.min(CURVE_BOTTOM, Math.max(CURVE_TOP - 4, p2[1] - (p3[1] - p1[1]) * t));
    d += ` C${p1[0] + (p2[0] - p0[0]) * t},${c1y} ${p2[0] - (p3[0] - p1[0]) * t},${c2y} ${p2[0]},${p2[1]}`;
  }
  return d;
}

const weekday = (date: IsoDate) => new Date(`${date}T12:00:00Z`).getUTCDay();
const isWeekend = (date: IsoDate) => {
  const wd = weekday(date);
  return wd === 0 || wd === 6;
};
const monthShort = new Intl.DateTimeFormat('fr-FR', { month: 'short', timeZone: 'UTC' });

const formatMm = (mm: number) => `${mm < 10 ? mm.toLocaleString('fr-FR', { maximumFractionDigits: 1 }) : Math.round(mm)} mm`;

export function Frise({ series, today, selected, onSelect, mode, label }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const dragging = useRef(false);
  const touchDrag = useRef(false);
  const titleId = useId();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => setWidth(entry!.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const days = series[0]?.days ?? [];
  const index = Math.max(0, days.findIndex((d) => d.date === selected));
  const todayIndex = days.findIndex((d) => d.date === today);
  const current = days[index];
  const col = width / Math.max(days.length, 1);

  const geometry = useMemo(() => {
    const x = (i: number) => col * (i + 0.5);
    const maxRain = Math.max(18, ...days.map((d) => d.rain_mm));
    const rain = scaleLinear().domain([0, maxRain]).range([0, RAIN_H]);
    // Maille : échelle absolue de 0 à 100. Zone : échelle serrée sur la plage de la frise,
    // sinon une moyenne de 18 à 24 resterait plate et l'on ne verrait pas la forêt « s'allumer ».
    const scores = series.flatMap((s) => s.days.map((d) => d.score));
    const lo = Math.min(...scores);
    const hi = Math.max(...scores);
    const span = Math.max(6, hi - lo);
    const domain = mode === 'maille' ? [0, 100] : [Math.max(0, lo - span * 0.5), hi + span * 0.2];
    const y = scaleLinear().domain(domain).range([CURVE_BOTTOM, CURVE_TOP]).clamp(true);
    const split = todayIndex >= 0 ? todayIndex : days.length - 1;
    const curves = series.map((s) => {
      const points = s.days.map((d, i) => [x(i), y(d.score)] as [number, number]);
      const past = points.slice(0, split + 1);
      const future = points.slice(split);
      return {
        species: s.species,
        points,
        past: smoothPath(past),
        future: future.length > 1 ? smoothPath(future) : '',
        area: past.length > 1 ? `${smoothPath(past)} L${past.at(-1)![0]},${CURVE_BOTTOM} L${past[0]![0]},${CURVE_BOTTOM} Z` : '',
      };
    });
    return { x, rain, curves };
  }, [series, days, col, mode, todayIndex]);

  const pick = (clientX: number) => {
    const rect = ref.current!.getBoundingClientRect();
    const i = Math.min(days.length - 1, Math.max(0, Math.floor(((clientX - rect.left) / rect.width) * days.length)));
    const date = days[i]?.date;
    if (date && date !== selected) {
      // Cran sous le doigt à chaque jour franchi (Android ; ignoré ailleurs).
      if (dragging.current && touchDrag.current) navigator.vibrate?.(4);
      onSelect(date);
    }
  };

  const onPointerDown = (e: PointerEvent<SVGSVGElement>) => {
    // Au doigt, pas de focus clavier sur la frise : certains navigateurs mobiles feraient défiler
    // le panneau pour l'amener à l'écran.
    if (e.pointerType !== 'mouse') e.preventDefault();
    touchDrag.current = e.pointerType === 'touch';
    dragging.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    pick(e.clientX);
  };
  const onPointerMove = (e: PointerEvent<SVGSVGElement>) => {
    if (dragging.current) pick(e.clientX);
  };
  const onPointerUp = () => {
    dragging.current = false;
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const moves: Record<string, number> = {
      ArrowLeft: index - 1,
      ArrowDown: index - 1,
      ArrowRight: index + 1,
      ArrowUp: index + 1,
      Home: 0,
      End: days.length - 1,
      PageUp: index + 7,
      PageDown: index - 7,
    };
    const next = moves[e.key];
    if (next == null) {
      if (today && (e.key === 't' || e.key === 'a')) {
        e.preventDefault();
        onSelect(today);
      }
      return;
    }
    e.preventDefault();
    const date = days[Math.min(days.length - 1, Math.max(0, next))]?.date;
    if (date) onSelect(date);
  };

  // Une seule structure, données ou pas : la coque prérendue et la frise chargée partagent les mêmes nœuds
  // (le texte change sans que le navigateur compte une nouvelle peinture du plus grand élément).
  const loaded = current != null && today != null;
  const forecast = current?.forecast ?? false;
  const scoreOf = (s: FriseSeries) => s.days[index]?.score ?? 0;
  const scoreText = series
    .map((s) => {
      const v = scoreOf(s);
      const name = series.length > 1 ? `${SPECIES[s.species].name.toLowerCase()} ` : mode === 'zone' ? 'score moyen ' : 'score ';
      return mode === 'maille' && series.length === 1 ? `${name}${v}, ${scoreLabel(v)}` : `${name}${v}`;
    })
    .join(' · ');
  const dayText = !current || current.date === today ? "Aujourd'hui" : formatLong(current.date).replace(/^./, (c) => c.toUpperCase());
  // Raccourcis au doigt : une colonne de la frise fait moins de 10 px sur un téléphone, trop peu pour viser
  // « samedi ». Aujourd'hui puis le prochain week-end ; mêmes nœuds avant le chargement, pour ne rien décaler.
  // Un jour du week-end hors de la prévision (on est dimanche) laisse sa place à « Demain ».
  const upcoming = todayIndex >= 0 ? days.slice(todayIndex + 1) : [];
  const weekendDay = (wd: number) => upcoming.find((d) => weekday(d.date) === wd)?.date ?? null;
  const tomorrow = upcoming[0]?.date ?? null;
  const named = (date: IsoDate) => `${formatWeekdayShort(date).replace(/^./, (c) => c.toUpperCase())} ${formatDayNumber(date)}`;
  const weekend = loaded ? [weekendDay(6), weekendDay(0)].filter((d): d is IsoDate => d != null) : [];
  const shortcuts: { key: string; date: IsoDate | null; text: string }[] = !loaded
    ? [
        { key: 'a', date: null, text: "Aujourd'hui" },
        { key: 'b', date: null, text: 'Sam.' },
        { key: 'c', date: null, text: 'Dim.' },
      ]
    : [
        { key: 'a', date: today, text: "Aujourd'hui" },
        ...(weekend.length < 2 && tomorrow && !weekend.includes(tomorrow) ? [{ key: 'demain', date: tomorrow, text: 'Demain' }] : []),
        ...weekend.map((d) => ({ key: d, date: d, text: named(d) })),
      ].slice(0, 3);
  // Dans la fiche, le jour et les scores sont déjà affichés au-dessus : la frise ne redit que la pluie.
  const summary = current
    ? mode === 'maille'
      ? `pluie ce jour-là : ${formatMm(current.rain_mm)}`
      : `pluie ${formatMm(current.rain_mm)} · ${scoreText}`
    : 'Pluie et pousse du dernier mois en cours de chargement…';
  const valueText = current
    ? `${formatLong(current.date)}${forecast ? ', prévision' : current.date === today ? ", aujourd'hui" : ''} : pluie ${formatMm(current.rain_mm)}, ${
        mode === 'zone' && series.length > 1 ? `score moyen ${scoreText}` : scoreText
      }`
    : 'chargement';

  return (
    <div className={`frise frise-${mode}`} data-series={series.length}>
      <div className="frise-entete" id={titleId}>
        {mode === 'zone' && (
          <p className="frise-jour">
            {dayText}
            {forecast && <span className="frise-prevision">prévision</span>}
          </p>
        )}
        <p className="frise-resume">{summary}</p>
      </div>
      <div
        ref={ref}
        className={loaded ? 'frise-trace' : 'frise-trace frise-trace-attente'}
        role="slider"
        tabIndex={loaded ? 0 : -1}
        aria-disabled={!loaded}
        aria-label={label}
        aria-describedby={titleId}
        aria-valuemin={0}
        aria-valuemax={Math.max(0, days.length - 1)}
        aria-valuenow={index}
        aria-valuetext={valueText}
        onKeyDown={onKeyDown}
      >
        {loaded && width > 0 && (
          <svg
            width={width}
            height={HEIGHT}
            viewBox={`0 0 ${width} ${HEIGHT}`}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            aria-hidden="true"
          >
            <defs>
              <pattern id={`${titleId}-prev`} width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <rect width="1.5" height="4" className="frise-motif" />
              </pattern>
            </defs>

            {/* Fin de semaine : « où aller ce week-end ». */}
            {days.map((d, i) =>
              isWeekend(d.date) ? <rect key={`we-${d.date}`} className="frise-weekend" x={i * col} y={0} width={col} height={HEIGHT} /> : null,
            )}

            <g className="frise-curseur" style={{ transform: `translateX(${index * col}px)` }}>
              <rect className="frise-curseur-bande" x={0} y={0} width={col} height={HEIGHT} rx={6} />
            </g>

            {/* Pluie, accrochée au bord haut. */}
            {days.map((d, i) => {
              const h = d.rain_mm >= 1 ? Math.max(3, geometry.rain(d.rain_mm)) : 0;
              return (
                <rect
                  key={`p-${d.date}`}
                  className={d.forecast ? 'frise-pluie frise-pluie-prevue' : 'frise-pluie'}
                  x={geometry.x(i) - Math.max(1.5, col * 0.3)}
                  y={0}
                  width={Math.max(3, col * 0.6)}
                  height={h}
                  rx={Math.min(3, col * 0.1)}
                  fill={d.forecast ? `url(#${titleId}-prev)` : undefined}
                />
              );
            })}

            <line className="frise-base" x1={0} x2={width} y1={CURVE_BOTTOM + 0.5} y2={CURVE_BOTTOM + 0.5} />

            {/* Pousse : aire pleine sur le passé, trait pointillé sur la prévision, une couleur par espèce. */}
            {geometry.curves.map((c) => (
              <g key={c.species} className="frise-espece" data-espece={c.species}>
                <path className="frise-aire" d={c.area} />
                <path className="frise-courbe" d={c.past} />
                {c.future && <path className="frise-courbe frise-courbe-prevue" d={c.future} />}
                {mode === 'maille' &&
                  series.length === 1 &&
                  c.points.map(([px, py], i) => {
                    const s = series.find((x) => x.species === c.species)!.days[i]!.score;
                    const band = s >= 80 ? 80 : s >= 60 ? 60 : s >= 40 ? 40 : s >= 20 ? 20 : 0;
                    return band ? <circle key={`m-${i}`} cx={px} cy={py} r={3.5} className="frise-point" style={{ fill: `var(--${c.species}-${band})` }} /> : null;
                  })}
              </g>
            ))}

            {todayIndex >= 0 && todayIndex < days.length - 1 && (
              <line
                className="frise-separateur"
                x1={(todayIndex + 1) * col}
                x2={(todayIndex + 1) * col}
                y1={CURVE_TOP - 2}
                y2={CURVE_BOTTOM}
              />
            )}

            <line className="frise-poignee-tige" x1={geometry.x(index)} x2={geometry.x(index)} y1={CURVE_TOP - 2} y2={CURVE_BOTTOM} />
            {geometry.curves.map((c) => {
              const knob = c.points[index];
              return knob ? (
                <g key={`k-${c.species}`} className="frise-poignee" data-espece={c.species} style={{ transform: `translate(${knob[0]}px, ${knob[1]}px)` }}>
                  <circle r={series.length > 1 ? 6 : 7} />
                </g>
              ) : null;
            })}

            {days.map((d, i) => {
              const cls = [
                'frise-date',
                i === index ? 'frise-date-choisie' : '',
                d.date === today ? 'frise-date-aujourdhui' : '',
                isWeekend(d.date) ? 'frise-date-weekend' : '',
              ].join(' ');
              // Colonnes larges : chaque jour est écrit. Colonnes étroites (un mois sur un téléphone) :
              // le mois en tête, puis les lundis, aujourd'hui et le jour choisi, sans chevauchement.
              const roomy = col >= 22;
              const firstOfMonth = i === 0 || d.date.slice(8) === '01';
              const showNumber = roomy || weekday(d.date) === 1 || d.date === today || i === index;
              // Priorité des numéros quand ils se touchent : le jour choisi, puis aujourd'hui (qui garde
              // aussi son point dessous), puis les lundis.
              const nearChosen = i !== index && Math.abs(i - index) < 3;
              const nearToday = d.date !== today && todayIndex >= 0 && Math.abs(i - todayIndex) < 3;
              const crowded = !roomy && i !== index && (nearChosen || nearToday);
              return (
                <g key={`l-${d.date}`} className={cls} transform={`translate(${geometry.x(i)}, ${CURVE_BOTTOM})`}>
                  {roomy ? (
                    <text y={16} textAnchor="middle" className="frise-semaine">
                      {formatWeekdayShort(d.date).charAt(0)}
                    </text>
                  ) : (
                    firstOfMonth && (
                      <text y={16} x={-col / 2} textAnchor="start" className="frise-mois">
                        {monthShort.format(new Date(`${d.date}T12:00:00Z`))}
                      </text>
                    )
                  )}
                  {showNumber && !crowded && (
                    <text y={33} textAnchor="middle" className="frise-numero">
                      {formatDayNumber(d.date)}
                    </text>
                  )}
                  {d.date === today && <circle cy={40} r={2.5} className="frise-point-aujourdhui" />}
                </g>
              );
            })}
          </svg>
        )}
      </div>
      <div className="frise-raccourcis" role="group" aria-label="Aller directement à un jour">
        {shortcuts.map((s) => (
          <button
            key={s.key}
            type="button"
            className="frise-raccourci"
            aria-pressed={loaded && s.date === current?.date}
            disabled={!loaded || !s.date}
            onClick={() => s.date && onSelect(s.date)}
          >
            {s.text}
          </button>
        ))}
      </div>
    </div>
  );
}
